import React, { useState } from "react";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import VideocamIcon from "@material-ui/icons/Videocam";
import VideocamOffIcon from "@material-ui/icons/VideocamOff";
import SecurityIcon from "@material-ui/icons/Security";
import LockIcon from "@material-ui/icons/Lock";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import { eventRoomStore } from "@/stores/event";
import { useEventRoomState } from "@/containers/root-container";
import "./moderation-control.scss";

/**
 * ModerationControl Component
 *
 * Contrôles de modération "Mute All" pour Host/Admin/Guide
 * Design: Warm Elegant (Travel Me Brand Colors)
 *
 * Règles métier:
 * - Host/Admin peuvent muter tous SAUF le guide
 * - Guide peut muter tous les résidents SAUF host/admin
 */
const ModerationControl: React.FC = () => {
  const eventRoomState = useEventRoomState();
  const myRole = eventRoomState.me.info.role;

  const audioInitialState = sessionStorage.getItem("audioAll")
    ? sessionStorage.getItem("audioAll")
    : "0";

  const videoInitialState = sessionStorage.getItem("videoAll")
    ? sessionStorage.getItem("videoAll")
    : "0";

  const [muteAudio, setAudio] = useState(audioInitialState);
  const [muteVideo, setVideo] = useState(videoInitialState);

  // Vérifier si l'utilisateur peut voir les contrôles de modération
  // Afficher uniquement pour les visites multi-connexion ET pour les rôles autorisés
  const isMultiConnection = eventRoomState.me.info.is_multiconnection;
  const canShowModerationControls = isMultiConnection && ['host', 'admin', 'guide'].includes(myRole);

  if (!canShowModerationControls) {
    return null;
  }

  const setAudioOrVideoState = (type: string, value: string) => {
    if (type === "audioAll") {
      sessionStorage.setItem(type, value);
    }
    if (type === "videoAll") {
      sessionStorage.setItem(type, value);
    }
  };

  const handleAudioForAll = () => {
    if (muteAudio === "1") {
      eventRoomStore.handleRemoteAudioForAll("unmute");
      setAudioOrVideoState("audioAll", "0");
      setAudio("0");
    } else {
      eventRoomStore.handleRemoteAudioForAll("mute");
      setAudioOrVideoState("audioAll", "1");
      setAudio("1");
    }
  };

  const handleVideoForAll = () => {
    if (muteVideo === "1") {
      eventRoomStore.handleRemoteVideoForAll("unmute");
      setAudioOrVideoState("videoAll", "0");
      setVideo("0");
    } else {
      eventRoomStore.handleRemoteVideoForAll("mute");
      setAudioOrVideoState("videoAll", "1");
      setVideo("1");
    }
  };

  const allMuted = muteAudio === "1";
  const allVideoMuted = muteVideo === "1";

  const getSubtext = () => {
    if (myRole === 'host' || myRole === 'admin') {
      return 'Controls affect all participants except the Guide.';
    }
    return 'Controls affect all Residents except Host/Admin.';
  };

  const getRoleLabel = () => {
    if (myRole === 'host') return 'Host';
    if (myRole === 'admin') return 'Admin';
    return 'Guide';
  };

  return (
    <>
      <style>{`
        .tm-moderation-container {
          --brand-coral: #E6C3B5;
          --brand-terracotta: #C07B64;
          --bg-cream: #FCF9F7;
          --text-deep: #3D3834;
          --text-muted: #8C847E;
          --soft-shadow: 0 10px 30px -10px rgba(192, 123, 100, 0.15);

          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: var(--bg-cream);
          padding: 16px;
          border-radius: 16px;
          border: 1px solid #E6C3B5;
          box-shadow: var(--soft-shadow);
          width: calc(100% - 20px);
          margin: 15px 10px;
          color: var(--text-deep);
          position: relative;
          overflow: hidden;
          box-sizing: border-box;
        }

        .tm-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        .tm-role-badge {
          background: white;
          border: 1px solid var(--brand-coral);
          color: var(--brand-terracotta);
          padding: 3px 10px;
          border-radius: 100px;
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .tm-title {
          font-size: 0.95rem;
          font-weight: 600;
          margin: 0;
          letter-spacing: -0.01em;
        }

        .tm-description {
          font-size: 0.75rem;
          color: var(--text-muted);
          line-height: 1.4;
          margin-bottom: 16px;
        }

        .tm-controls-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .tm-control-card {
          background: white;
          border: 1px solid rgba(230, 195, 181, 0.2);
          padding: 16px 12px;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
        }

        .tm-control-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 16px -6px rgba(192, 123, 100, 0.2);
          border-color: var(--brand-coral);
        }

        .tm-control-card:active {
          transform: translateY(0);
        }

        .tm-icon-wrapper {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: inherit;
          position: relative;
        }

        .tm-control-card.is-active .tm-icon-wrapper {
          background: var(--brand-coral);
          color: white;
          box-shadow: 0 4px 10px rgba(230, 195, 181, 0.4);
        }

        .tm-control-card.not-active .tm-icon-wrapper {
          background: #F3EEEC;
          color: var(--brand-terracotta);
        }

        .tm-control-label {
          font-size: 0.75rem;
          font-weight: 600;
          margin: 0;
        }

        .tm-status-tag {
          font-size: 0.6rem;
          font-weight: 500;
          padding: 2px 6px;
          border-radius: 4px;
          margin-top: 2px;
        }

        .tm-control-card.is-active .tm-status-tag {
          background: rgba(192, 123, 100, 0.1);
          color: var(--brand-terracotta);
        }

        .tm-control-card.not-active .tm-status-tag {
          background: rgba(76, 175, 80, 0.1);
          color: #4CAF50;
        }

        .tm-footer-info {
          margin-top: 16px;
          padding-top: 12px;
          border-top: 1px solid rgba(230, 195, 181, 0.2);
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }

        .tm-footer-right {
          color: var(--brand-terracotta);
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.65rem;
          font-weight: 600;
        }

        .tm-muted-indicator {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 8px;
          height: 8px;
          background: #E66A55;
          border: 2px solid white;
          border-radius: 50%;
        }
      `}</style>

      <div className="tm-moderation-container">
        <div className="tm-header">
          <div className="tm-role-badge">
            <SecurityIcon style={{ fontSize: 12 }} />
            {getRoleLabel()}
          </div>
          <h2 className="tm-title">Moderation</h2>
        </div>

        <p className="tm-description">{getSubtext()}</p>

        <div className="tm-controls-grid">
          {/* Audio Control */}
          <div
            className={`tm-control-card ${allMuted ? 'is-active' : 'not-active'}`}
            onClick={handleAudioForAll}
          >
            <div className="tm-icon-wrapper">
              {allMuted ? (
                <MicOffIcon style={{ fontSize: 20 }} />
              ) : (
                <MicIcon style={{ fontSize: 20 }} />
              )}
              {allMuted && <div className="tm-muted-indicator" />}
            </div>
            <div>
              <p className="tm-control-label">{allMuted ? 'All Muted' : 'Audio Open'}</p>
              <span className="tm-status-tag">
                {allMuted ? 'Restricted' : 'Live'}
              </span>
            </div>
          </div>

          {/* Video Control */}
          <div
            className={`tm-control-card ${allVideoMuted ? 'is-active' : 'not-active'}`}
            onClick={handleVideoForAll}
          >
            <div className="tm-icon-wrapper">
              {allVideoMuted ? (
                <VideocamOffIcon style={{ fontSize: 20 }} />
              ) : (
                <VideocamIcon style={{ fontSize: 20 }} />
              )}
              {allVideoMuted && <div className="tm-muted-indicator" />}
            </div>
            <div>
              <p className="tm-control-label">{allVideoMuted ? 'Video Off' : 'Video On'}</p>
              <span className="tm-status-tag">
                {allVideoMuted ? 'Privacy' : 'Live'}
              </span>
            </div>
          </div>
        </div>

        <div className="tm-footer-info">
          <div className="tm-footer-right">
            {allMuted && allVideoMuted ? (
              <>
                <LockIcon style={{ fontSize: 12 }} />
                <span>Privacy Locked</span>
              </>
            ) : (
              <>
                <LockOpenIcon style={{ fontSize: 12 }} />
                <span>Interactive</span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModerationControl;
